// useJourney — the reader's own trajectory through KTP.
//
// The framework's lineage model (Tethered → Divergent → Persistent) is
// repurposed as the reader's progress:
//
//   Tethered   — just arrived. Under a sponsor's trust (the author's).
//                < 40% of must-know concepts read.
//   Divergent  — operating independently. Understands the core.
//                ≥ 40% < 80% of must-know concepts read.
//   Persistent — can sponsor new readers. Full participation.
//                ≥ 80% of must-know concepts read.
//
// All progress lives in localStorage under "ktp.journey.v1". Zero server.
// Zero telemetry. Readers can reset anytime.
//
// Schema v2 (May 2026): adds authored journey support — userType,
// activeJourneyId, journeyStartedAt. Bumping SCHEMA_VERSION wipes
// prior v1 state rather than reading a now-invalid payload.

import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { UserType } from "../data/journeys/index";

const STORAGE_KEY = "ktp.journey.v1";
const SCHEMA_VERSION = 2;

export type LineageStage = "tethered" | "divergent" | "persistent";

export type JourneyState = {
  /** Set of read-item ids (e.g. "concept:tensor", "journey:parent:phone-free-zones"). */
  read: Set<string>;
  /** ISO timestamp when the reader first arrived. */
  startedAt: string;
  /** Which user type this reader self-identified as on /start. */
  userType: UserType | null;
  /** ID of the authored journey currently in progress. */
  activeJourneyId: string | null;
  /** ISO timestamp when the current journey was started. */
  journeyStartedAt: string | null;
};

/* ── External store pattern so every subscribed component rerenders
     when any component writes to localStorage. ────────────────────── */

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l();
}

type PersistedShape = {
  version: number;
  read: string[];
  startedAt: string;
  userType: UserType | null;
  activeJourneyId: string | null;
  journeyStartedAt: string | null;
};

function freshState(): JourneyState {
  return {
    read: new Set(),
    startedAt: new Date().toISOString(),
    userType: null,
    activeJourneyId: null,
    journeyStartedAt: null,
  };
}

function load(): JourneyState {
  if (typeof window === "undefined") return freshState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const fresh = freshState();
      save(fresh);
      return fresh;
    }
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    if (parsed.version !== SCHEMA_VERSION || !Array.isArray(parsed.read)) {
      const fresh = freshState();
      save(fresh);
      return fresh;
    }
    return {
      read: new Set(parsed.read),
      startedAt:
        typeof parsed.startedAt === "string"
          ? parsed.startedAt
          : new Date().toISOString(),
      userType: parsed.userType ?? null,
      activeJourneyId: parsed.activeJourneyId ?? null,
      journeyStartedAt: parsed.journeyStartedAt ?? null,
    };
  } catch {
    return freshState();
  }
}

function save(state: JourneyState) {
  if (typeof window === "undefined") return;
  const payload: PersistedShape = {
    version: SCHEMA_VERSION,
    read: [...state.read],
    startedAt: state.startedAt,
    userType: state.userType,
    activeJourneyId: state.activeJourneyId,
    journeyStartedAt: state.journeyStartedAt,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

/** The single source of truth used by useSyncExternalStore. */
let currentState: JourneyState | null = null;

function getState(): JourneyState {
  if (currentState === null) currentState = load();
  return currentState;
}

function setState(next: JourneyState) {
  currentState = next;
  save(next);
  notify();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      currentState = load();
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/* ── Public API ───────────────────────────────────── */

export type JourneyAPI = {
  read: Set<string>;
  startedAt: string;
  userType: UserType | null;
  activeJourneyId: string | null;
  isRead: (id: string) => boolean;
  markRead: (id: string) => void;
  unmarkRead: (id: string) => void;
  toggleRead: (id: string) => void;
  reset: () => void;
  /** How many of the given stop IDs are marked read. */
  completedStops: (stopIds: string[]) => number;
  setUserType: (t: UserType) => void;
  setActiveJourney: (id: string) => void;
  clearActiveJourney: () => void;
  /** Percent progress through the must-know set (0..1). Drives stage. */
  mustKnowPercent: (mustKnowIds: string[]) => number;
  /** Percent progress through the full concept set (0..1). */
  progressPercent: (allIds: string[]) => number;
  /** Current lineage stage derived from the must-know percent. */
  stage: (mustKnowIds: string[]) => LineageStage;
};

export function stageFromPercent(p: number): LineageStage {
  if (p >= 0.8) return "persistent";
  if (p >= 0.4) return "divergent";
  return "tethered";
}

export function stageLabel(stage: LineageStage): string {
  if (stage === "persistent") return "Persistent";
  if (stage === "divergent") return "Divergent";
  return "Tethered";
}

export function stageDescription(stage: LineageStage): string {
  if (stage === "persistent")
    return "You carry the core. Sponsor new readers; extend the framework.";
  if (stage === "divergent")
    return "You understand the spine. Trajectory building. Keep weathering.";
  return "Newly arrived. Under a sponsor's trust while you build E_base.";
}

export function useJourney(): JourneyAPI {
  const state = useSyncExternalStore(subscribe, getState, getState);

  const markRead = useCallback((id: string) => {
    const s = getState();
    if (s.read.has(id)) return;
    const next = { ...s, read: new Set(s.read) };
    next.read.add(id);
    setState(next);
  }, []);

  const unmarkRead = useCallback((id: string) => {
    const s = getState();
    if (!s.read.has(id)) return;
    const next = { ...s, read: new Set(s.read) };
    next.read.delete(id);
    setState(next);
  }, []);

  const toggleRead = useCallback((id: string) => {
    const s = getState();
    const next = { ...s, read: new Set(s.read) };
    if (next.read.has(id)) next.read.delete(id);
    else next.read.add(id);
    setState(next);
  }, []);

  const reset = useCallback(() => {
    setState(freshState());
  }, []);

  const setUserType = useCallback((t: UserType) => {
    setState({ ...getState(), userType: t });
  }, []);

  const setActiveJourney = useCallback((id: string) => {
    setState({
      ...getState(),
      activeJourneyId: id,
      journeyStartedAt: new Date().toISOString(),
    });
  }, []);

  const clearActiveJourney = useCallback(() => {
    setState({ ...getState(), activeJourneyId: null, journeyStartedAt: null });
  }, []);

  return {
    read: state.read,
    startedAt: state.startedAt,
    userType: state.userType,
    activeJourneyId: state.activeJourneyId,
    isRead: (id: string) => state.read.has(id),
    markRead,
    unmarkRead,
    toggleRead,
    reset,
    completedStops: (stopIds: string[]) =>
      stopIds.filter((id) => state.read.has(id)).length,
    setUserType,
    setActiveJourney,
    clearActiveJourney,
    mustKnowPercent: (ids: string[]) => {
      if (ids.length === 0) return 0;
      return ids.filter((id) => state.read.has(id)).length / ids.length;
    },
    progressPercent: (ids: string[]) => {
      if (ids.length === 0) return 0;
      return ids.filter((id) => state.read.has(id)).length / ids.length;
    },
    stage: (ids: string[]) =>
      stageFromPercent(
        ids.length === 0
          ? 0
          : ids.filter((id) => state.read.has(id)).length / ids.length
      ),
  };
}

/** Opt-in auto-mark when the user has dwelt on a doc page for N ms. */
export function useAutoMarkRead(id: string, dwellMs = 8000) {
  const { markRead, isRead } = useJourney();
  useEffect(() => {
    if (isRead(id)) return;
    const t = window.setTimeout(() => markRead(id), dwellMs);
    return () => window.clearTimeout(t);
  }, [id, dwellMs, markRead, isRead]);
}
